import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { API } from '@models/API';

import { patchableSignal } from '@utils/signals/patchableSignal';

/**
 * Element-wise comparison of two priority sets. Both sides are already
 * deduplicated and sorted ascending, so the order is deterministic and a
 * positional walk is enough.
 */
const samePriorities = (a: number[], b: number[]) =>
  a.length === b.length && a.every((priority, i) => priority === b[i]);

@Injectable({ providedIn: 'root' })
export class OperationsState {
  private router = inject(Router);

  public _operations = patchableSignal<API.Trusts.Operations.Response>({
    items: [],
    total: 0,
    page: 0,
    size: 0,
    pages: 0,
  });

  /** El `trust_number` del segmento de la ruta, con la barra. */
  private _segment = signal('');

  /**
   * Cuál de las operaciones del trust se pidió, de `?lender=` (LEN-3164).
   *
   * Se usa el lender y no el `operation_id` porque ese id no llega al front:
   * `find_all_list` lo borra antes de serializar. Vacío en un link viejo, y
   * ahí la resolución es la de antes.
   */
  private _requestedLender = signal('');
  private _isActive = signal(false);
  private _activeChild = signal<string>('');
  private _selectedPriorities = signal<number[] | null>(null);

  activeOperation = computed(() => {
    const trustNumber = this._segment();
    const requested = this._requestedLender();
    // LEN-3164: buscar por `trust_number` dejó de identificar una operación.
    // Desde LEN-3159 un trust puede tener dos --una por lender-- y este
    // `find` devolvía la que el listado trajera primero, que para un usuario
    // de CXC puede ser la del otro lender: la pantalla entera (reportes,
    // insights, concentrations) quedaba mostrando datos ajenos sin ninguna
    // señal.
    //
    // Sin `?lender=` --links viejos, favoritos, los de notificaciones
    // (LEN-3163)-- la respuesta es la de antes, correcta mientras el trust
    // tenga una sola operación.
    //
    // Devuelve `undefined` cuando todavía no hay nada que resolver, y lo dice
    // en el tipo: el `!` que había antes afirmaba lo contrario mientras
    // `activeId` lo consumía con `?.`, y esa mentira es la que dejaba pasar un
    // `undefined` hasta `getCompanyIdFormFacility`.
    return this.get().items.find(
      op =>
        op.trust_number === trustNumber &&
        (!requested || op.lender === requested)
    );
  });

  availableTopics = computed(
    () => this.activeOperation()?.available_topics ?? []
  );

  private defaultPriority = computed(() => {
    const topics = this.availableTopics();
    return topics.length ? Math.min(...topics.map(t => t.priority)) : 0;
  });

  /**
   * The Sections the user has selected. Defaults to the lowest available
   * priority. Never empty.
   *
   * `equal` keeps the previous array when the resulting set is unchanged, so
   * re-selecting the same Sections is not a notification. Consumers fan a
   * change out into one request per Section, so a spurious notification costs
   * N redundant HTTP calls.
   */
  selectedPriorities = computed(
    () => {
      const selection = this._selectedPriorities() ?? [this.defaultPriority()];
      const topics = this.availableTopics();

      // No topics means the operations list has not loaded yet (or was
      // cleared), not that every Section is gone: there is nothing to clamp
      // against, so hold the selection until the refetch lands. Same reading
      // as `defaultPriority`, which falls back to 0 in that window.
      if (!topics.length) {
        return selection;
      }

      // A Section the user had selected can disappear on a refetch (a facility
      // edit re-issues the list without navigating). Drop what no longer
      // exists, and never let the set end up empty.
      const available = new Set(topics.map(t => t.priority));
      const existing = selection.filter(priority => available.has(priority));

      return existing.length ? existing : [this.defaultPriority()];
    },
    { equal: samePriorities }
  );

  /**
   * Lowest selected priority. Kept as a scalar because most consumers still
   * drive a single-Section request off it.
   */
  selectedPriority = computed(() => Math.min(...this.selectedPriorities()));

  selectedTopic = computed(
    () =>
      this.availableTopics().find(t => t.priority === this.selectedPriority())
        ?.topic ?? null
  );

  constructor() {
    this.router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        const operationIsActive = this.router.isActive(
          '/dashboard/portfolio/facility',
          {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
          }
        );

        if (operationIsActive) {
          // La query se separa antes de partir por `/`: si no, el último
          // segmento se lleva el `?operation=...` pegado y `activeChild`
          // quedaría como `insights?operation=abc` (LEN-3164).
          const [path, queryString = ''] = this.router.url.split('?');
          const url = path.split('/');

          if (url.length >= 6) {
            const previousId = this._segment();
            this._isActive.set(true);
            // El segmento se guarda CRUDO (LEN-3164): puede ser un
            // `operation_id` --lo que mandan los links desde LEN-3159-- o un
            // `trust_number` con guiones, de un link viejo. Traducirlo a trust
            // acá obligaba a decidir cuál era antes de poder mirarlo;
            // `activeOperation` compara contra los dos y no necesita saberlo.
            try {
              this._segment.set(
                decodeURIComponent(url[4]).replaceAll('-', '/')
              );
            } catch {
              this._segment.set(url[4].replaceAll('-', '/'));
            }
            this._requestedLender.set(
              new URLSearchParams(queryString).get('lender') ?? ''
            );
            this._activeChild.set(url[5]);
            if (this._segment() !== previousId) {
              this._selectedPriorities.set(null);
            }
            return;
          }
        }

        this._isActive.set(false);
        this._segment.set('');
        this._requestedLender.set('');
        this._activeChild.set('');
        this._selectedPriorities.set(null);
      }
    });
  }

  get: Signal<API.Trusts.Operations.Response> = this._operations.asReadonly();

  set(operations: API.Trusts.Operations.Response) {
    this._operations.set(operations);
  }

  /**
   * Empties the cached list. Needed after a facility mutation: operationGuard
   * short-circuits on a known trust_number, so a stale entry here would keep
   * the detail pages showing pre-edit values.
   */
  clear() {
    this._operations.set({ items: [], total: 0, page: 0, size: 0, pages: 0 });
  }

  patch(values: Partial<API.Trusts.Operations.Response>) {
    this._operations.patch(values);
  }

  /**
   * El TRUST NUMBER de la operación activa, con la barra: `F/1554`.
   *
   * Lo consumen `reportsAPI.query`, `contractsAPI.query`, el Directory y el
   * Overview, todos como trust. Por eso sale de la operación resuelta y no del
   * segmento de la ruta, que desde LEN-3164 puede ser un `operation_id`.
   *
   * Vacío mientras la operación no esté resuelta. Antes caía al segmento de
   * la ruta, y eso le entregaba a `reportsAPI.query`, `contractsAPI.query`, el
   * Directory, el Overview y el `facility_id` de Pendo un valor que esos
   * consumidores tratan como trust sin que lo fuera todavía.
   */
  activeId = computed(() => this.activeOperation()?.trust_number ?? '');

  isActive = this._isActive.asReadonly();

  isIdActive(id: string) {
    return id.replace('/', '-') === this.activeId().replace('/', '-');
  }

  selectPriority(priority: number) {
    this.selectPriorities([priority]);
  }

  /**
   * Replaces the whole selection. An empty set is ignored: `selectedPriority`
   * has to stay a valid number for the consumers that still read it as a
   * scalar, so at least one Section must always be selected.
   */
  selectPriorities(priorities: number[]) {
    const next = [...new Set(priorities)].sort((a, b) => a - b);

    if (!next.length) {
      return;
    }

    this._selectedPriorities.set(next);
  }

  // Refers to which sub-page is active
  activeChild = this._activeChild.asReadonly();
}
